import React, { useState } from 'react';
import { Info, AlertTriangle, CheckCircle2, ChevronRight, Eye, Timer, Brain, Zap } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · M13 · Détection & franchissement d'intersections
// Œuvre pédagogique interactive :
//  · 4 types d'intersection sous forme d'onglets cliquables
//  · Vue de dessus SVG avec signalisation + voitures + priorité highlightée
//  · Séquence R-R-D-F personnalisée pour CHAQUE type d'intersection
//  · Panneau : qui a la priorité, ce que je dois faire, piège classique, sanction
// ══════════════════════════════════════════════════════════════════════════

const INTERSECTIONS = [
  {
    id: 'feu',
    label: 'Carrefour à feu',
    icone: '🚦',
    color: '#ef4444',
    signalPos: { x: 320, y: 200 }, // position du feu
    signalType: 'lights',
    priorite: "Feu VERT",
    prioriteDetail: "J'ai le droit de passer SI la voie est libre et sûre. Le vert ne dispense JAMAIS de la vigilance.",
    ralentir: "Anticiper à distance : orange = préparer à s'arrêter. Vert = maintenir l'allure sans accélérer.",
    regarder: "Piétons qui finissent leur passage, tourne-à-gauche adverses, feu clignotant orange, comportement des autres véhicules.",
    decider: "Vert franc et voie libre → je passe. Orange ou vert clignotant → je m'arrête SI je peux le faire sans danger.",
    franchir: "Vitesse constante, sans hésitation. Ne PAS s'arrêter au milieu du carrefour même si le feu passe rouge.",
    piege: "Le tourne-à-gauche : au vert, si tu tournes à gauche, tu dois céder aux véhicules venant EN FACE en priorité. Beaucoup d'accidents à ce moment.",
    sanction: "Franchissement au rouge : 135 € + 4 points + 3 ans de suspension possible",
    egoStart: { x: 435, y: 620 },
    adverseCars: [{ x: 365, y: 100, color: '#3b82f6', dir: 'south' }],
  },
  {
    id: 'stop',
    label: 'Carrefour STOP',
    icone: '🛑',
    color: '#dc2626',
    signalPos: { x: 350, y: 400 },
    signalType: 'stop',
    priorite: "AUCUNE — arrêt obligatoire",
    prioriteDetail: "Je DOIS marquer un arrêt complet (roues immobiles) sur la ligne blanche, quel que soit l'état de la circulation.",
    ralentir: "Ralentir progressivement dès la vue du panneau (à ~50 m). Rétrograder en 2ème vers 30 m.",
    regarder: "Après l'arrêt SEULEMENT : regarder à gauche → à droite → à gauche. Au moins 2 sec par regard.",
    decider: "Céder la priorité à TOUS les véhicules venant sur la route principale, dans les 2 sens. Repérer une brèche sûre (7 sec minimum).",
    franchir: "Passer la 1ère → embrayage → franchir franchement pour libérer l'intersection rapidement.",
    piege: "Le \"rolling stop\" (ralentir sans marquer l'arrêt). L'arrêt DOIT être complet, roues à zéro, même si la visibilité est parfaite.",
    sanction: "Non-respect du stop : 135 € + 4 points en moins",
    egoStart: { x: 435, y: 620 },
    adverseCars: [{ x: 100, y: 335, color: '#f59e0b', dir: 'east' }, { x: 700, y: 265, color: '#22c55e', dir: 'west' }],
  },
  {
    id: 'cedez',
    label: 'Cédez le passage',
    icone: '⚠️',
    color: '#f59e0b',
    signalPos: { x: 350, y: 400 },
    signalType: 'yield',
    priorite: "Céder aux véhicules sur la route principale",
    prioriteDetail: "PAS d'arrêt obligatoire si la voie est libre. Je peux passer SANS m'arrêter si aucun véhicule ne vient sur la route principale.",
    ralentir: "Ralentir suffisamment pour ÉVALUER la circulation. Ne pas s'arrêter systématiquement (créer un embouteillage).",
    regarder: "Gauche et droite en approche, sans forcément s'arrêter.",
    decider: "Voie libre → je continue à vitesse d'insertion. Véhicule à distance de sécurité → je passe. Véhicule proche → je m'arrête et attends.",
    franchir: "Fluide, sans rupture de rythme si la voie est libre.",
    piege: "Confondre avec le STOP. Le triangle pointe en bas = céder (souvent PAS besoin de s'arrêter). L'octogone rouge = arrêt obligatoire.",
    sanction: "Refus de priorité : 135 € + 4 points en moins",
    egoStart: { x: 435, y: 620 },
    adverseCars: [{ x: 100, y: 335, color: '#f59e0b', dir: 'east' }],
  },
  {
    id: 'droite',
    label: 'Priorité à droite',
    icone: '↩️',
    color: '#22c55e',
    signalPos: null, // pas de signalisation = règle par défaut
    signalType: 'none',
    priorite: "Celui qui vient de MA DROITE est prioritaire",
    prioriteDetail: "Règle par défaut à toute intersection SANS signalisation. Si un véhicule débouche à ma droite, je dois lui céder le passage.",
    ralentir: "Systématique en zone résidentielle sans panneau. Vitesse d'approche : 30 km/h maximum.",
    regarder: "À DROITE en priorité (celle qui compte), puis à gauche. Contrôle-épaule si besoin.",
    decider: "Aucun véhicule à droite → je passe. Véhicule à droite qui approche → je cède, même s'il roule vite.",
    franchir: "Prudence maintenue jusqu'à la sortie complète du carrefour.",
    piege: "Oublier la règle en agglomération. Beaucoup d'accidents dans les lotissements où la règle par défaut s'applique — même si tu venais d'une \"grande rue\".",
    sanction: "Refus de priorité à droite : 135 € + 4 points en moins",
    egoStart: { x: 435, y: 620 },
    adverseCars: [{ x: 700, y: 265, color: '#8b5cf6', dir: 'west' }],
  },
];

// ══════════════════════════════════════════════════════════════════════════
// Composants SVG internes
// ══════════════════════════════════════════════════════════════════════════

// Voiture vue de dessus (simplifiée)
function Voiture({ x, y, color, rotation = 0, isEgo = false }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotation})`}>
      <ellipse cx={0} cy={2} rx={22} ry={4} fill="#000" opacity={0.3} />
      <rect x={-18} y={-30} width={36} height={60} rx={13} fill={color} stroke="#0b1120" strokeWidth={2} />
      <ellipse cx={0} cy={-14} rx={13} ry={9} fill="#111" opacity={0.7} />
      <ellipse cx={0} cy={14} rx={12} ry={7} fill="#111" opacity={0.6} />
      <circle cx={-10} cy={-25} r={3} fill="#fef3c7" />
      <circle cx={10} cy={-25} r={3} fill="#fef3c7" />
      {isEgo && (
        <>
          <circle cx={0} cy={-40} r={11} fill="#fff" stroke={color} strokeWidth={2} />
          <text x={0} y={-36} textAnchor="middle" fontSize={10} fontWeight="bold" fill={color}>MOI</text>
        </>
      )}
    </g>
  );
}

// Signalisation
function Signal({ type, x, y }) {
  if (type === 'lights') {
    return (
      <g transform={`translate(${x} ${y})`}>
        <rect x={-10} y={-30} width={20} height={70} rx={4} fill="#0b1120" stroke="#374151" strokeWidth={1.5} />
        <circle cx={0} cy={-18} r={5} fill="#4b5563" />
        <circle cx={0} cy={0} r={5} fill="#4b5563" />
        <circle cx={0} cy={18} r={5} fill="#22c55e">
          <animate attributeName="opacity" values="1;0.5;1" dur="1.5s" repeatCount="indefinite" />
        </circle>
      </g>
    );
  }
  if (type === 'stop') {
    return (
      <g transform={`translate(${x} ${y})`}>
        <polygon points="0,-25 18,-18 25,0 18,18 0,25 -18,18 -25,0 -18,-18" fill="#dc2626" stroke="#fff" strokeWidth={3} />
        <text x={0} y={5} textAnchor="middle" fontSize={12} fontWeight="900" fontFamily="Arial" fill="#fff">STOP</text>
      </g>
    );
  }
  if (type === 'yield') {
    return (
      <g transform={`translate(${x} ${y})`}>
        <polygon points="0,25 25,-18 -25,-18" fill="#fff" stroke="#dc2626" strokeWidth={4} />
      </g>
    );
  }
  return null;
}

// ══════════════════════════════════════════════════════════════════════════
// Composant principal
// ══════════════════════════════════════════════════════════════════════════

export default function IntersectionsSvg() {
  const [selected, setSelected] = useState('feu');
  const current = INTERSECTIONS.find((i) => i.id === selected);

  const rrdf = [
    { lettre: 'R', mot: 'Ralentir', desc: current.ralentir, color: '#f59e0b', icon: Timer },
    { lettre: 'R', mot: 'Regarder', desc: current.regarder, color: '#3b82f6', icon: Eye },
    { lettre: 'D', mot: 'Décider', desc: current.decider, color: '#8b5cf6', icon: Brain },
    { lettre: 'F', mot: 'Franchir', desc: current.franchir, color: '#22c55e', icon: Zap },
  ];

  return (
    <div className="w-full">
      {/* Onglets des 4 types d'intersection */}
      <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {INTERSECTIONS.map((i) => (
          <button
            key={i.id}
            type="button"
            onClick={() => setSelected(i.id)}
            data-testid={`m13-type-${i.id}`}
            className={`h-14 rounded-xl border-2 transition text-[12.5px] font-bold px-2 flex items-center justify-center gap-2 ${
              selected === i.id
                ? 'text-white shadow-lg'
                : 'border-white/15 bg-white/[0.02] text-white/70 hover:border-white/30'
            }`}
            style={selected === i.id ? { borderColor: i.color, background: i.color + '22', boxShadow: `0 8px 24px ${i.color}44` } : {}}
          >
            <span className="text-[18px]">{i.icone}</span>
            <span className="leading-tight">{i.label}</span>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr,360px] gap-4">
        {/* SVG principal */}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#1a2434] to-[#0a1220]">
          <svg viewBox="0 0 800 720" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label={`Intersection ${current.label}`}>
            <defs>
              <linearGradient id="int-grass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4d7c3a" />
                <stop offset="100%" stopColor="#365828" />
              </linearGradient>
              <linearGradient id="int-road" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3f4b5c" />
                <stop offset="100%" stopColor="#2b3546" />
              </linearGradient>
            </defs>

            {/* Herbe (4 coins) */}
            <rect width={800} height={720} fill="url(#int-grass)" />

            {/* Croix routière */}
            <rect x={0}   y={230} width={800} height={140} fill="url(#int-road)" />
            <rect x={330} y={0}   width={140} height={720} fill="url(#int-road)" />

            {/* Marquage central pointillé */}
            <line x1={400} y1={0}   x2={400} y2={230} stroke="#fff" strokeWidth={4} strokeDasharray="20 20" opacity={0.7} />
            <line x1={400} y1={370} x2={400} y2={720} stroke="#fff" strokeWidth={4} strokeDasharray="20 20" opacity={0.7} />
            <line x1={0}   y1={300} x2={330} y2={300} stroke="#fff" strokeWidth={4} strokeDasharray="20 20" opacity={0.7} />
            <line x1={470} y1={300} x2={800} y2={300} stroke="#fff" strokeWidth={4} strokeDasharray="20 20" opacity={0.7} />

            {/* Ligne d'arrêt (pour STOP/Cédez sur la branche sud) */}
            {(current.id === 'stop') && (
              <line x1={330} y1={385} x2={470} y2={385} stroke="#fff" strokeWidth={7} />
            )}
            {(current.id === 'cedez') && (
              <>
                <line x1={330} y1={385} x2={340} y2={385} stroke="#fff" strokeWidth={5} />
                <line x1={355} y1={385} x2={365} y2={385} stroke="#fff" strokeWidth={5} />
                <line x1={380} y1={385} x2={390} y2={385} stroke="#fff" strokeWidth={5} />
                <line x1={405} y1={385} x2={415} y2={385} stroke="#fff" strokeWidth={5} />
                <line x1={430} y1={385} x2={440} y2={385} stroke="#fff" strokeWidth={5} />
                <line x1={455} y1={385} x2={465} y2={385} stroke="#fff" strokeWidth={5} />
              </>
            )}

            {/* Signalisation */}
            {current.signalType !== 'none' && current.signalPos && (
              <Signal type={current.signalType} x={current.signalPos.x} y={current.signalPos.y} />
            )}
            {current.signalType === 'none' && (
              <g transform="translate(700 40)">
                <rect x={-80} y={-16} width={160} height={32} rx={16} fill="#0b1120" stroke="#22c55e" strokeWidth={2} />
                <text x={0} y={5} textAnchor="middle" fill="#22c55e" fontSize={12} fontWeight="bold">Aucune signalisation</text>
              </g>
            )}

            {/* Flèches indiquant qui a la priorité */}
            {current.adverseCars.map((adv, i) => {
              const isPriority = (current.id === 'stop' || current.id === 'cedez')
                || (current.id === 'droite' && adv.dir === 'west'); // priorité droite ↔ vient de ma droite
              return (
                <g key={i}>
                  {isPriority && (
                    <g opacity={0.85}>
                      <circle cx={adv.x} cy={adv.y - 45} r={13} fill="#22c55e" />
                      <text x={adv.x} y={adv.y - 40} textAnchor="middle" fill="#fff" fontSize={13} fontWeight="900">✓</text>
                    </g>
                  )}
                  <Voiture
                    x={adv.x} y={adv.y}
                    color={adv.color}
                    rotation={adv.dir === 'east' ? 90 : adv.dir === 'west' ? -90 : adv.dir === 'south' ? 180 : 0}
                  />
                </g>
              );
            })}

            {/* Voiture EGO ("moi") */}
            <Voiture x={current.egoStart.x} y={current.egoStart.y} color="#ef4444" rotation={0} isEgo />

            {/* Bandeau bas */}
            <g transform="translate(0, 680)">
              <rect width={800} height={40} fill="#000" opacity={0.65} />
              <text x={20} y={26} fill="#fff" fontSize={14} fontWeight="bold">M13 · Détection intersections</text>
              <text x={20} y={39} fill="#94a3b8" fontSize={11}>REMC C2c — Détecter, identifier et franchir les intersections</text>
              <circle cx={770} cy={20} r={16} fill="#dc2626" />
              <text x={770} y={25} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>
        </div>

        {/* Panneau droit */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="m13-panel">
          <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Intersection sélectionnée</div>
          <h4 className="mt-1 font-serif-editorial text-white text-[20px] leading-tight flex items-center gap-2">
            <span className="text-[22px]">{current.icone}</span>{current.label}
          </h4>

          {/* Priorité */}
          <div className="mt-4 rounded-lg border-2 p-3.5" style={{ borderColor: current.color, background: current.color + '18' }}>
            <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: current.color }}>🎯 Qui a la priorité ?</div>
            <div className="mt-1 text-white font-semibold text-[14px]">{current.priorite}</div>
            <p className="mt-1.5 text-white/80 text-[12.5px] leading-relaxed">{current.prioriteDetail}</p>
          </div>

          {/* Séquence R-R-D-F */}
          <div className="mt-4">
            <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-2">Séquence R · R · D · F</div>
            <div className="space-y-1.5">
              {rrdf.map((r, i) => {
                const Icon = r.icon;
                return (
                  <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5" data-testid={`m13-rrdf-${i}`}>
                    <div className="flex items-start gap-2">
                      <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-serif-editorial text-[16px] font-bold" style={{ background: r.color + '25', color: r.color }}>
                        {r.lettre}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-white font-semibold text-[13px]">
                          <Icon size={11} style={{ color: r.color }} /> {r.mot}
                        </div>
                        <p className="mt-0.5 text-white/70 text-[11.5px] leading-relaxed">{r.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Piège */}
          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Piège classique</div>
              <p className="mt-0.5 text-white/85 text-[12px] leading-relaxed">{current.piege}</p>
            </div>
          </div>

          {/* Sanction */}
          <div className="mt-3 rounded-lg border border-red-500/40 bg-red-950/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-red-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Sanction</div>
              <p className="mt-0.5 text-white/85 text-[12px] leading-relaxed">{current.sanction}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
